import { FC, useEffect, useRef } from 'react';
import { ROUTES } from 'src/constants/routes.const';
import { useContinueToNextPathStep } from 'src/hooks/useContinueToNextPathStep';
import useSafePush from 'src/hooks/useSafePush';
import { PathBeforeNavigate } from '../PathContinueButtons';
import { PreCheckpointScreen } from '../PreCheckpointScreen';
import { PathSessionType } from '../types';
import * as S from './styles';

export type PathAutoContinueProps = {
  languagePairId: number | null;
  pathwayNodeId?: number | null;
  sessionType?: PathSessionType | null;
  /** Lets the caller hold the launch behind an interstitial, as with `PathContinueButtons`. */
  onBeforeNavigate?: PathBeforeNavigate;
};

/**
 * Takes the learner straight into their next path step on mount, as if they had pressed
 * "Continue learning" - without making them stop on a screen to do it.
 */
export const PathAutoContinue: FC<PathAutoContinueProps> = ({
  languagePairId,
  pathwayNodeId = null,
  sessionType = null,
  onBeforeNavigate,
}) => {
  const { safePush } = useSafePush();
  const { continueToNextStep, pendingCheckpointUrl, confirmCheckpoint, isLaunchingCheckpoint } =
    useContinueToNextPathStep({
      languagePairId,
      pathwayNodeId,
      pathwayCheckpointId: null,
      sessionType,
    });
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    if (onBeforeNavigate) {
      onBeforeNavigate(continueToNextStep, 'continue');
    } else {
      continueToNextStep();
    }
  }, [continueToNextStep, onBeforeNavigate]);

  if (pendingCheckpointUrl && languagePairId) {
    return (
      <PreCheckpointScreen
        onContinue={confirmCheckpoint}
        isContinuing={isLaunchingCheckpoint}
        onBack={() => safePush(ROUTES.path)}
      />
    );
  }

  return (
    <S.LoaderWrapper data-testid="path-auto-continue">
      <S.Loader />
    </S.LoaderWrapper>
  );
};
